import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import { useTranslation } from "react-i18next";
import Sidebar from "../../components/sidebar/Sidebar";
import { useSelector, useDispatch } from "react-redux";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { components } from "../../types/api";
import { useGetInsertionOrdersByCampaignQuery } from "../../store/apis/insertionOrderApi";
import {
  useBulkUpsertCustomEntityNamesMutation,
  useGetCustomEntityNamesByTypeQuery,
} from "../../store/apis/customEntitiesApi";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";

// Types from API
type EntityType = components["schemas"]["EntityType"];
type ApiResponseInsertionOrderWithRelationsArray =
  components["schemas"]["ApiResponse_InsertionOrderWithRelations-Array_"];
type CustomEntityNameWithOriginalArray =
  components["schemas"]["ApiResponse_CustomEntityNameWithOriginal-Array_"];
type BulkCustomEntityNameItem =
  components["schemas"]["BulkCustomEntityNameItem"];

const EditIONames: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const collapsed = useSelector(selectSidebarCollapsed);
  const dispatch = useDispatch();

  const { data: ioResp, isLoading: isLoadingIOs } =
    useGetInsertionOrdersByCampaignQuery(campaignId || "", {
      skip: !campaignId,
    });

  // Load all existing custom names for INSERTIONORDER
  const { data: customNamesResp, isLoading: isLoadingCustomNames } =
    useGetCustomEntityNamesByTypeQuery("INSERTIONORDER");

  const insertionOrders = useMemo(
    () =>
      (ioResp?.data ?? []) as NonNullable<
        ApiResponseInsertionOrderWithRelationsArray["data"]
      >,
    [ioResp?.data]
  );

  // Map existing custom names by entityId for quick lookup
  const existingCustomNameById = useMemo(() => {
    const map = new Map<string, string>();
    const items = (customNamesResp?.data ?? []) as NonNullable<
      CustomEntityNameWithOriginalArray["data"]
    >;
    items.forEach((item) => {
      if (item.entityId && item.customName) {
        map.set(item.entityId, item.customName);
      }
    });
    return map;
  }, [customNamesResp?.data]);

  // Local form state for custom names
  const [formValues, setFormValues] = useState<Record<string, string>>({});

  // Initialize form values when data loads
  useEffect(() => {
    const initial: Record<string, string> = {};
    insertionOrders.forEach((io) => {
      const id = io.id ?? "";
      if (!id) return;
      initial[id] = existingCustomNameById.get(id) ?? "";
    });
    setFormValues(initial);
  }, [insertionOrders, existingCustomNameById]);

  const [bulkUpsert, { isLoading: isSaving }] =
    useBulkUpsertCustomEntityNamesMutation();

  const handleBack = () => {
    navigate(`/campaigns/${campaignId}/edit`);
  };

  const handleChange = (id: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [id]: value }));
  };

  const handleSave = async () => {
    // Prepare items: only include non-empty custom names
    const items: BulkCustomEntityNameItem[] = Object.entries(formValues)
      .map(([entityId, customName]) => ({
        entityId,
        customName: customName.trim(),
      }))
      .filter((x) => x.customName.length > 0);

    if (items.length === 0) {
      dispatch(
        setToastMessage({
          message: t("customNames.nothingToSave", {
            defaultValue: "Nothing to save",
          }),
          severity: "info",
        })
      );
      return;
    }

    try {
      const entityType: EntityType = "INSERTIONORDER" as EntityType;
      const res = await bulkUpsert({ entityType, items }).unwrap();
      const created = res.data?.created ?? 0;
      const updated = res.data?.updated ?? 0;
      dispatch(
        setToastMessage({
          message: t("customNames.saved", {
            defaultValue: `Saved IO names (${created} created, ${updated} updated)`,
          }),
          severity: "success",
        })
      );
    } catch (e) {
      console.error(e);
      dispatch(
        setToastMessage({
          message: t("customNames.saveError", {
            defaultValue: "Failed to save IO names",
          }),
          severity: "error",
        })
      );
    }
  };

  const isLoading = isLoadingIOs || isLoadingCustomNames;

  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar />
      <Box
        sx={{
          flexGrow: 1,
          px: 4,
          py: 2,
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <PageBreadcrumbs />

        {/* Header */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
          <Typography
            variant="h5"
            sx={{
              fontFamily: "Montserrat",
              fontWeight: 700,
              color: theme.palette.grey[900],
            }}
          >
            {t("customNames.editIONames", {
              defaultValue: "Edit Insertion Order Names",
            })}
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              variant="outlined"
              sx={{
                borderRadius: 50,
                textTransform: "none",
                fontFamily: "Nunito",
                borderColor: theme.palette.grey[200],
                color: theme.palette.grey[700],
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: "transparent",
                },
              }}
            >
              {t("common.back", { defaultValue: "Back" })}
            </Button>
            <Button
              startIcon={<SaveIcon />}
              onClick={handleSave}
              variant="contained"
              color="primary"
              disabled={isSaving || isLoading}
              sx={{
                borderRadius: 50,
                textTransform: "none",
                fontFamily: "Nunito",
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {isSaving
                ? t("common.saving", { defaultValue: "Saving..." })
                : t("common.save", { defaultValue: "Save" })}
            </Button>
          </Box>
        </Box>

        {/* Content */}
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {insertionOrders.length === 0 ? (
              <Typography variant="body1" color="text.secondary">
                {t("customNames.noIOs", {
                  defaultValue: "No insertion orders found for this campaign.",
                })}
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {insertionOrders.map((io) => {
                  const id = io.id ?? "";
                  const originalName = io.displayName ?? "";
                  const value = formValues[id] ?? "";
                  return (
                    <Grid item xs={12} md={6} key={id}>
                      <Box
                        sx={{
                          p: 2,
                          border: `1px solid ${theme.palette.neutral[300]}`,
                          borderRadius: 2,
                          backgroundColor: theme.palette.background.paper,
                        }}
                      >
                        <Typography
                          variant="pSmall"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          {t("customNames.originalName", {
                            defaultValue: "Original name",
                          })}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ mb: 1.5, fontWeight: 600 }}
                        >
                          {originalName}
                        </Typography>
                        <TextField
                          fullWidth
                          label={t("customNames.customName", {
                            defaultValue: "Custom name",
                          })}
                          value={value}
                          onChange={(e) => handleChange(id, e.target.value)}
                          placeholder={
                            t("customNames.customNamePlaceholder", {
                              defaultValue:
                                "Enter a custom name (leave empty to keep original)",
                            }) as string
                          }
                        />
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default EditIONames;
